import axios from 'axios';
import { Task } from '../types/Task';

const BASE_URL = process.env.REACT_APP_API_URL;

export interface AllTasksTaskListResponse {
  data: Task[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

/**
 * Every list filter is multiselect. A plain string is still accepted (deep links and older
 * callers) and is serialised unchanged; arrays go on the wire comma-separated, which is the
 * convention the rest of the app already uses.
 */
export interface AllTasksFilters {
  serviceArea?: string | string[];
  projectId?: string | string[];
  taskType?: string | string[];
  status?: string | string[];
  foremanArea?: string | string[];
  contractYear?: string | string[];
  activityId?: string | string[];
  search?: string;
  taskId?: string;
}

export interface AllTasksFilterOptions {
  statuses: string[];
  taskTypes: string[];
  serviceAreas: Array<{ id: number; name: string }>;
  contractYears: string[];
  projects: Array<{ id: number; name: string; projectNumber: string; serviceAreaId?: number }>;
  foremanAreas: Array<{ id: number; name: string; serviceAreaId?: number; serviceAreaName?: string }>;
  activities: Array<{ id: number; activityName: string; activityNumber: string }>;
}

class AllTasksService {
  private handleError(error: unknown): never {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { error?: string; details?: string } | undefined;
      throw new Error(data?.error || data?.details || error.message || 'Request failed');
    }
    throw error instanceof Error ? error : new Error('Request failed');
  }

  async getAllTasksPaginated(
    page: number = 1,
    pageSize: number = 20,
    filters: AllTasksFilters = {},
    sort?: { sortBy: string; sortOrder: 'asc' | 'desc' }
  ): Promise<AllTasksTaskListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      // Sorting has to reach the server: the list is server-paginated, so ordering only the
      // current page would sort 20 rows out of the whole result set.
      if (sort) {
        params.set('sortBy', sort.sortBy);
        params.set('sortOrder', sort.sortOrder);
      }
      Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
          return;
        }
        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else {
          params.set(key, value.toString());
        }
      });
      const response = await axios.get(`${BASE_URL}/tasks/all?${params}`, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAllTasksFilterOptions(): Promise<AllTasksFilterOptions> {
    try {
      const response = await axios.get(`${BASE_URL}/tasks/all/filter-options`, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export const allTasksService = new AllTasksService();
export default allTasksService;
